import { ArrowUpRight, ChevronUp, X } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { abandonOpportunity, type WebOpportunity } from '../../../lib/opportunities';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { Card, IconButton, TextButton, Typography } from '../ui';
import styles from './OpportunitiesExpanded.module.css';

// The full opportunities list, opened in place by "View More (N)" on the pile — the section morphs from
// the three-card peek stack into a vertical list of EVERY proposal, spanning the band's full width so
// its cards aren't squeezed into a third-column. Mirrors native's `OpportunitiesDeck` expanded state:
// a "Opportunities" header + subtitle, one full card per opportunity (workspace eyebrow, title,
// "Talk to … ↗"), each carrying a ✕ that ABANDONS it — the only dismiss affordance in the feature — and
// a centered "View Less" that collapses back to the pile.

const PAGE = 'home';

interface Props {
  opportunities: WebOpportunity[];
  workspaceName: (opportunity: WebOpportunity) => string | undefined;
  onViewLess: () => void;
}

export function OpportunitiesExpanded({ opportunities, workspaceName, onViewLess }: Props) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();

  const viewLess = () => {
    logPageClickBi(
      { page_name: PAGE, button_name: 'opportunities_view_less' },
      { componentName: 'opportunities-view-less' },
    );
    onViewLess();
  };

  return (
    <section className={styles.expanded} aria-label={t('home.opportunities.panelTitle')}>
      <div className={styles.list}>
        {opportunities.map((opportunity, i) => (
          <OpportunityRow
            key={opportunity.id}
            opportunity={opportunity}
            workspace={workspaceName(opportunity)}
            index={i}
          />
        ))}
      </div>
      <div className={styles.footer}>
        <TextButton
          size="small"
          className={styles.viewToggle}
          endIcon={<ChevronUp size={18} aria-hidden />}
          onClick={viewLess}
        >
          {t('home.opportunities.viewLess')}
        </TextButton>
      </div>
    </section>
  );
}

function OpportunityRow({
  opportunity,
  workspace,
  index,
}: {
  opportunity: WebOpportunity;
  workspace?: string;
  index: number;
}) {
  const { t } = useTranslation();
  const { short } = useAgentName();
  const { logPageClickBi } = useGenericBi();
  const { openChatWithMessage } = useActiveConversation();

  const talk = () => {
    logPageClickBi({ page_name: PAGE, button_name: 'opportunity_talk' }, { componentName: 'opportunity-talk' });
    openChatWithMessage({ kind: 'new', message: t('home.opportunities.chatPrompt', { title: opportunity.title }) });
  };

  const dismiss = () => {
    logPageClickBi({ page_name: PAGE, button_name: 'opportunity_dismiss' }, { componentName: 'opportunity-dismiss' });
    void abandonOpportunity(opportunity.id);
  };

  // The whole card is the hit target — clicking anywhere opens the chat (the DS `interactive` skin gives
  // the stone hover/press state). "Talk to … ↗" is the visual affordance, not its own control (a button
  // cannot nest inside the card button). The ✕ is a SIBLING overlay, so dismissing never also talks.
  // Cap the stagger so a long list doesn't fling the last rows in from far away (native's `min(index, 4)`).
  return (
    <div className={styles.rowWrap} style={{ '--row-index': Math.min(index, 4) } as React.CSSProperties}>
      <Card variant="sunken" size="lg" interactive component="button" className={styles.row} onClick={talk}>
        {workspace && (
          <Typography variant="h4" color="strong" component="span" noWrap className={styles.eyebrow}>
            {workspace}
          </Typography>
        )}
        <Typography variant="h2" color="strong" component="span" className={styles.title}>
          {opportunity.title}
        </Typography>
        <span className={styles.cta}>
          {t('home.opportunities.cta', { name: short })}
          <ArrowUpRight size={16} aria-hidden />
        </span>
      </Card>
      <IconButton
        size="small"
        variant="text"
        className={styles.dismiss}
        aria-label={t('home.opportunities.dismiss', { title: opportunity.title })}
        onClick={dismiss}
      >
        <X size={20} aria-hidden />
      </IconButton>
    </div>
  );
}
